import { AfterViewInit, ChangeDetectorRef, Directive, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { CheckoutService } from '@df/checkout/checkout.service';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/api/customer';
import { LoginFormComponentDef } from '@df/session/login/login-form.component.def';
import { Login } from '@df/session/login/login.interfaces';
import { NeedLoginService } from '@df/session/need-login.service';

@Directive()
export abstract class CheckoutAccountComponentDef extends LoginFormComponentDef implements OnInit, OnDestroy, AfterViewInit {
    /** Dummy variable for validation */
    dummy: any;

    /**
     * If true then will advance to next checkout step
     * when account step is valid
     * @type {boolean}
     */
    @Input()
    autoAdvance = false;

    /**
     * If true then will advance to next checkout step on init
     * when customer alread has an account
     * @type {boolean}
     */
    @Input()
    autoAdvanceOnAccount = false;

    /**
     * Is account required flag
     * If true then we will consider step as invalid till customer is logged in
     *
     * We will not allow guest checkout either
     *
     * @type {bool}
     */
    @Input()
    accountRequired = false;

    /**
     * Step form  (check also ngOnDestroy)
     * important: you need to put #stepForm="ngForm" on form element in template
     */
    protected _stepForm?: NgForm;
    @ViewChild('stepForm', { static: false })
    set stepForm(form: NgForm) {
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.checkoutStepComponent.addForm(form);
        this._stepForm = form;
    }

    /**
     * Status model for logout
     */
    readonly logoutStatus = new Status();

    /**
     * If was valid last time we checked
     */
    protected wasValid = false;

    protected emailFieldName?: string;

    /**
     * If items in basket should be kept when logging out
     */
    protected preserverQuoteOnLogout = true;

    protected checkAutoAdvanceThrothlled = this.timeoutService.throttle(() => this.checkAutoAdvance(), 100, {
        leading: false,
        trailing: true
    });

    @NgInject(CheckoutService)
    protected checkout: CheckoutService;

    /**
     * Class constructor
     */
    constructor(
        public readonly checkoutFormComponent: CheckoutFormComponentDef,
        public readonly checkoutStepComponent: CheckoutStepComponentDef,
        private requestService: RequestService,
        customer: Customer,
        app: App,
        ref: ChangeDetectorRef,
        needLoginService: NeedLoginService,
        protected timeoutService: TimeoutService
    ) {
        super(ref, requestService, customer, app, needLoginService);
    }

    /////////////////////////////////////////////
    //
    // ACTIONS
    //
    /////////////////////////////////////////////

    logout() {
        return this.requestService.send(
            this.logoutStatus,
            () => this.requestService.nexus.checkout.logout({ preserve_quote: this.preserverQuoteOnLogout }),
            () => {
                this.checkout.reset();
                this.checkoutFormComponent.resetData();
                this.onLogoutSuccess();
            }
        );
    }

    override login(onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.requestService.submitForm(
            this.form,
            this.loginStatus,
            () => this.requestService.nexus.checkout.login(this.data as Login.ILoginFormData),
            onSuccess,
            onError
        );
    }

    protected onLogoutSuccess(): void {
        if (this.data) {
            this.data.password = undefined;
        }
        this.email = undefined;
    }

    /////////////////////////////////////////////
    //
    // EVENTS
    //
    /////////////////////////////////////////////

    /**
     * Set up component
     */
    override ngOnInit(): void {
        super.ngOnInit();
        this.onCheckoutReload();
        this.subscriptions.add(this.checkoutFormComponent.subscribe((change: Checkout.IStateChange) => this.onCheckoutChange(change)));
        this.subscriptions.add(
            this.app.events.on<Checkout.IStepChangeEventData>(Checkout.EVENTS.StepChange).subscribe(() => this.detectChanges())
        );
        this.onCheckoutChange({ full: true });
    }

    /**
     * If is valid initially then advance
     */
    ngAfterViewInit(): void {
        // start from next step if logged in
        if (this.autoAdvanceOnAccount && this.checkoutStepComponent && this.isLoggedIn && this.checkoutStepComponent.valid) {
            this.performAutoAdvance();
        }
        this.wasValid = false;
    }

    protected performAutoAdvance(): void {
        const nextStep = this.checkoutFormComponent.getNextStep(this.checkoutStepComponent);
        const currentStep = this.checkoutFormComponent.currentStep;
        if (nextStep && (!currentStep || nextStep.order > currentStep.order)) {
            this.checkoutFormComponent.goToStep(nextStep);
        }
    }

    protected checkAutoAdvance(): void {
        // auto advance support
        const valid = this.isValid && (!this.checkoutStepComponent || this.checkoutStepComponent.valid);
        if (valid && !this.wasValid) {
            if (this.autoAdvance) {
                this.performAutoAdvance();
            }
            this.wasValid = valid;
            this.checkoutFormComponent.detectChanges();
        }
    }

    /**
     * Cleanup
     */
    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.checkoutStepComponent.removeForm(this._stepForm);
    }

    /**
     * Called on checkout relad
     */
    protected onCheckoutReload(): void {
        this.data = this.checkoutFormComponent.data.account;
        this.email = this.checkoutFormComponent.email || this.checkout.checkoutEmail;
    }

    /**
     * Called when email is changed
     * Leaves guest checkout if change
     */
    protected onEmailChange(newEmail: string | undefined, oldEmail: string | undefined): void {
        if (newEmail) {
            this.checkout.checkoutEmail = newEmail;
        }

        if (!newEmail || (oldEmail && newEmail !== oldEmail)) {
            this.leaveGuestCheckout();
        }

        return super.onEmailChange(newEmail, oldEmail);
    }

    /**
     * Called on checkout backend data changes (patches, init etc)
     */
    protected onCheckoutChange(change: Checkout.IStateChange): void {
        if (change.full) {
            const userData = this.checkoutFormComponent.backendData.user;
            if (!userData) {
                // with thing below we were redirected to login step on any checkout save request save (like shipping address)
                // this.checkoutFormComponent.toggleGuestCheckout(false);
            } else {
                this.data.email = userData.email;
                this.setCheckResult(userData);
            }
        }
    }
    /////////////////////////////////////////////
    //
    // STATUS
    //
    /////////////////////////////////////////////

    /**
     * Checks if client should regiter
     */
    shouldRegister(): boolean {
        return this.accountRequired && this.isNewUser();
    }

    isNewUser(): boolean {
        return super.shouldRegister();
    }

    /**
     * Checks if client should log in
     */
    shouldLogin(): boolean {
        return super.shouldLogin() && !this.isGuestCheckout;
    }

    /**
     * Checks if all valid
     */
    get isValid(): boolean {
        return this.getIsValid();
    }

    ngAfterViewChecked(): void {
        this.timeoutService.setTimeout(() => {
            this.checkAutoAdvanceThrothlled();
        }, 1000);
    }

    /////////////////////////////////////////////
    //
    // GUEST CHECKOUT
    //
    /////////////////////////////////////////////

    /**
     * Checks if guest checkout is used
     */
    get isGuestCheckout(): boolean {
        return !this.accountRequired && this.checkoutFormComponent.isGuestCheckout && this.isChecked() && this.hasAccount();
    }

    /**
     * To guest checkout
     */
    setGuestCheckout(): void {
        this.checkoutFormComponent.toggleGuestCheckout(true);
    }

    /**
     * To leave guest checkout
     */
    leaveGuestCheckout(): void {
        if (this.form) {
            this.form.submitAttempt = false;
        }
        this.checkoutFormComponent.toggleGuestCheckout(false);
    }

    canUseGuestCheckout() {
        return !this.accountRequired && this.shouldLogin();
    }

    protected getIsValid(): boolean {
        if (this.emailFieldName && this.form) {
            const emailField = this.form.controls[this.emailFieldName];
            if (emailField && (emailField.invalid || emailField.pending)) {
                return false;
            }
        }
        if (this.customer.model.isLoggedIn) {
            return true;
        }

        if (this.checkStatus.busy) {
            if (this.email && this.checkoutFormComponent.isGuestCheckout && !this.accountRequired) {
                return true;
            }
            return false;
        }
        if (this.accountRequired) {
            return false;
        }
        if (!this.checkResult) {
            return false;
        }
        if (this.hasAccount() && !this.isGuestCheckout) {
            return false;
        }
        return true;
    }
}
